<template>
	<v-card>
		<v-card-title class="d-flex align-center pa-3 pb-1">
			<v-icon icon="mdi-home-thermometer" class="mr-2" />
			<div class="d-flex flex-column" style="min-width: 0;">
				<span class="text-truncate">{{ name }}</span>
				<span v-if="sourceLabel" class="text-caption text-medium-emphasis" style="line-height: 1.1;">{{ sourceLabel }}</span>
			</div>
			<v-spacer />
			<v-chip v-if="!hasData" color="warning" size="x-small">no data</v-chip>
			<v-chip v-else-if="isStale" color="warning" size="x-small">stale</v-chip>
		</v-card-title>

		<v-card-text class="pa-3 pt-0">
			<div v-if="hasData" class="d-flex align-baseline flex-wrap ga-3">
				<div class="room-temp text-primary">{{ fmt(temp) }}°F</div>
				<div v-if="humidity != null" class="d-flex align-center text-medium-emphasis">
					<v-icon icon="mdi-water-percent" size="small" class="mr-1" />
					<span class="text-body-1">{{ humidity }}%</span>
				</div>
				<v-spacer />
				<div class="text-caption text-medium-emphasis">
					neutral {{ neutral }}°F · {{ deltaLabel }}
				</div>
			</div>

			<div v-else class="text-caption text-medium-emphasis py-2">
				<div v-if="usingMocks">No room sample yet — wait for the first loop tick.</div>
				<div v-else>
					SmartThings unreachable. Check <code>SMARTTHINGS_TOKEN</code> + <code>SMARTTHINGS_DEVICE_ID</code> in <code>.env</code> (PATs expire after 24h), or toggle SmartThings to mock in Settings.
				</div>
			</div>
		</v-card-text>
	</v-card>
</template>

<script setup>
import { computed } from "vue";
import statusStore from "../stores/statusStore.js";

var d = computed(() => statusStore.data.data);
var name = computed(() => d.value?.config?.roomName ?? "Bedroom");
var temp = computed(() => d.value?.room?.temp);
var humidity = computed(() => d.value?.room?.humidity);
var neutral = computed(() => d.value?.config?.roomNeutralTemp ?? 72);
var usingMocks = computed(() => d.value?.useMocks?.smartthings ?? false);

// Short visual confirmation of which sensor we're polling. Maps known device IDs
// to friendly names; falls back to the first 8 chars of the UUID for anything
// new. Update the lookup if the device ID in .env changes.
var KNOWN_DEVICES = {
	"9c21fb11-17ea-4b1c-967f-1887f8b2815c": "Bedroom (ecobee)",
	"27b6c151-dcaa-4208-8a86-d9dfe03be380": "Bedroom (ecobee, dead)",
	"0fbb7fa7-3b1c-49e8-b1f2-38559bde2e40": "Master Bedroom Motion (z-wave)",
};
var sourceLabel = computed(() => {
	var id = d.value?.room?.deviceId;
	if (!id) { return null; }
	return "via " + (KNOWN_DEVICES[id] ?? id.slice(0, 8));
});

var hasData = computed(() => temp.value != null);

var isStale = computed(() => {
	var ts = d.value?.ts;
	if (!ts) { return false; }
	var ageMs = Date.now() - ts;
	var loopMs = (d.value?.config?.loopIntervalSeconds ?? 60) * 1000;
	return ageMs > loopMs * 3;
});

var deltaLabel = computed(() => {
	if (temp.value == null) { return ""; }
	var delta = temp.value - neutral.value;
	if (Math.abs(delta) < 0.5) { return "neutral"; }
	var dir = delta > 0 ? "warmer" : "cooler";
	return Math.abs(delta).toFixed(1) + "° " + dir;
});

function fmt(v) {
	if (v == null) { return "—"; }
	return Number(v).toFixed(1);
}
</script>

<style scoped>
.room-temp {
	font-size: 2.25rem;
	font-weight: 600;
	line-height: 1.1;
}
@media (max-width: 599px) {
	.room-temp {
		font-size: 1.875rem;
	}
}
</style>
